/**
 * Sticker della chat associati (spec 2026-10-25 §4-§6): tipi del catalogo e dei messaggi, testo
 * d'anteprima, misure nella bolla, ricerca e «Recenti» per persona (localStorage, sempre in try/catch).
 *
 * I tipi sono dichiarati qui (non in `shared/types/chat.ts`, che aggiorna il backend) con la stessa forma
 * del contratto: `tipo` del messaggio si confronta come stringa, così vale con e senza `'sticker'` nel tipo condiviso.
 */

/** Sticker in un messaggio (`ChatMessaggio.sticker`): `url` firmato senza scadenza. */
export interface StickerMessaggio {
  id: string;
  pacchetto: string;
  url: string;
  animato: boolean;
  larghezza: number | null;
  altezza: number | null;
  emoji: string[];
}

/** Uno sticker del catalogo di una conversazione (`GET /api/chat/sticker`). */
export interface StickerVoce {
  id: string;
  url: string;
  animato: boolean;
  larghezza: number | null;
  altezza: number | null;
  emoji: string[];
  parole: string;
}

/** Pacchetto del catalogo: solo ciò che la persona può inviare in quella conversazione. */
export interface PacchettoSticker {
  id: string;
  nome: string;
  riservato: boolean;
  copertina: { id: string; url: string } | null;
  sticker: StickerVoce[];
}

export interface CatalogoSticker {
  versione: string | null;
  pacchetti: PacchettoSticker[];
}

/** Evento socket del namespace `/chat`: il catalogo è cambiato. */
export const EVENTO_STICKER_AGGIORNATI = 'sticker-aggiornati';

/** Lato massimo dello sticker nella bolla (px). */
export const LATO_STICKER = 160;
/** Recenti tenuti per persona. */
export const MAX_RECENTI = 24;

/** Il messaggio (del server o locale) è uno sticker. */
export const eSticker = (m: unknown): boolean => {
  if (!m || typeof m !== 'object') return false;
  const x = m as { tipo?: unknown; sticker?: unknown; id?: unknown };
  if (String(x.tipo) === 'sticker') return true;
  // messaggio locale in invio (senza `id` né `tipo`) con lo sticker scelto
  return x.id === undefined && x.tipo === undefined && !!x.sticker;
};

/**
 * Lo sticker da mostrare. Senza il campo `sticker` (risposta inattesa) si ripiega sull'`allegato` di
 * compatibilità, che ha lo stesso `url`: un messaggio `tipo: 'sticker'` non diventa mai una foto.
 */
export const stickerDi = (m: unknown): StickerMessaggio | null => {
  if (!m || typeof m !== 'object') return null;
  const x = m as { sticker?: any; allegato?: any };
  const s = x.sticker;
  if (s && typeof s === 'object' && typeof s.url === 'string' && s.url) {
    return {
      id: String(s.id || ''),
      pacchetto: String(s.pacchetto || ''),
      url: s.url,
      animato: !!s.animato,
      larghezza: typeof s.larghezza === 'number' ? s.larghezza : null,
      altezza: typeof s.altezza === 'number' ? s.altezza : null,
      emoji: Array.isArray(s.emoji) ? s.emoji.filter((e: unknown) => typeof e === 'string') : [],
    };
  }
  const a = x.allegato;
  if (eSticker(m) && a && typeof a === 'object' && typeof a.url === 'string' && a.url) {
    return {
      id: String(a.id || ''),
      pacchetto: '',
      url: a.url,
      animato: false,
      larghezza: typeof a.larghezza === 'number' ? a.larghezza : null,
      altezza: typeof a.altezza === 'number' ? a.altezza : null,
      emoji: [],
    };
  }
  return null;
};

/** Testo d'anteprima (elenco, citazioni): «😂 Sticker» con la prima emoji, altrimenti «🏷️ Sticker». */
export const testoAnteprimaSticker = (emoji?: string[] | null): string =>
  emoji && emoji.length && emoji[0] ? `${emoji[0]} Sticker` : '🏷️ Sticker';

/** Anteprima di un messaggio sticker. */
export const anteprimaStickerDi = (m: unknown): string => {
  const s = stickerDi(m);
  return testoAnteprimaSticker(s ? s.emoji : null);
};

/** Misure nella bolla: lato massimo 160 px, proporzioni conservate (quadrato se ignote). */
export const misureSticker = (larghezza: number | null, altezza: number | null): { width: number; height: number } => {
  if (!larghezza || !altezza || larghezza <= 0 || altezza <= 0) return { width: LATO_STICKER, height: LATO_STICKER };
  // anche uno sticker piccolo occupa il lato massimo, come nelle app di messaggistica
  const fattore = LATO_STICKER / Math.max(larghezza, altezza);
  return { width: Math.round(larghezza * fattore), height: Math.round(altezza * fattore) };
};

/** Nome accessibile di uno sticker: «Sticker 😂 🎉» o «Sticker». */
export const nomeSticker = (emoji?: string[] | null): string =>
  emoji && emoji.length ? `Sticker ${emoji.join(' ')}` : 'Sticker';

/** La conversazione ammette gli sticker: diretta sempre, canale con `canale.sticker === true`. */
export const stickerNellaConversazione = (c: { tipo: string; canale?: unknown } | null | undefined): boolean => {
  if (!c) return false;
  if (c.tipo === 'diretta') return true;
  const canale = c.canale as { sticker?: unknown } | null | undefined;
  return !!canale && canale.sticker === true;
};

/** Risposta del server ripulita: pacchetti con almeno uno sticker valido. */
export const normalizzaCatalogo = (data: any): CatalogoSticker => {
  const pacchetti: PacchettoSticker[] = [];
  const elenco = data && Array.isArray(data.pacchetti) ? data.pacchetti : [];
  elenco.forEach((p: any) => {
    if (!p || typeof p.id !== 'string') return;
    const sticker: StickerVoce[] = (Array.isArray(p.sticker) ? p.sticker : [])
      .filter((s: any) => s && typeof s.id === 'string' && typeof s.url === 'string' && s.url)
      .map((s: any) => ({
        id: s.id,
        url: s.url,
        animato: !!s.animato,
        larghezza: typeof s.larghezza === 'number' ? s.larghezza : null,
        altezza: typeof s.altezza === 'number' ? s.altezza : null,
        emoji: Array.isArray(s.emoji) ? s.emoji.filter((e: unknown) => typeof e === 'string') : [],
        parole: typeof s.parole === 'string' ? s.parole : '',
      }));
    if (!sticker.length) return;
    const copertina = p.copertina && typeof p.copertina.url === 'string' ? { id: String(p.copertina.id || ''), url: p.copertina.url } : null;
    pacchetti.push({ id: p.id, nome: typeof p.nome === 'string' ? p.nome : '', riservato: !!p.riservato, copertina, sticker });
  });
  return { versione: data && typeof data.versione === 'string' ? data.versione : null, pacchetti };
};

/** Tutti gli sticker del catalogo, senza doppioni, nell'ordine dei pacchetti. */
export const tuttiGliSticker = (pacchetti: PacchettoSticker[]): StickerVoce[] => {
  const visti = new Set<string>();
  const out: StickerVoce[] = [];
  pacchetti.forEach((p) => p.sticker.forEach((s) => {
    if (visti.has(s.id)) return;
    visti.add(s.id);
    out.push(s);
  }));
  return out;
};

const normalizza = (t: string): string =>
  String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/** Ricerca per emoji (anche più di una) e per parole chiave, su tutto il catalogo. */
export const cercaSticker = (pacchetti: PacchettoSticker[], q: string): StickerVoce[] => {
  const testo = normalizza(q);
  if (!testo) return [];
  return tuttiGliSticker(pacchetti).filter((s) => {
    if (s.emoji.some((e) => q.includes(e) || e.includes(q.trim()))) return true;
    return !!s.parole && normalizza(s.parole).includes(testo);
  });
};

// --- Recenti (localStorage per persona) ---

export const chiaveRecenti = (persona: string | null | undefined): string => `chat-sticker-recenti:${persona || 'anonimo'}`;

export const leggiRecenti = (persona: string | null | undefined): string[] => {
  try {
    const grezzo = window.localStorage.getItem(chiaveRecenti(persona));
    if (!grezzo) return [];
    const valore = JSON.parse(grezzo);
    return Array.isArray(valore) ? valore.filter((v) => typeof v === 'string').slice(0, MAX_RECENTI) : [];
  } catch (e) {
    return [];
  }
};

/** Mette lo sticker in cima ai recenti (al più 24) e restituisce l'elenco nuovo, anche se il salvataggio non riesce. */
export const aggiungiRecente = (persona: string | null | undefined, id: string): string[] => {
  const elenco = [id, ...leggiRecenti(persona).filter((x) => x !== id)].slice(0, MAX_RECENTI);
  try {
    window.localStorage.setItem(chiaveRecenti(persona), JSON.stringify(elenco));
  } catch (e) {
    // memoria del browser non disponibile: i recenti valgono solo finché il pannello resta aperto
  }
  return elenco;
};

/** Recenti ancora inviabili in questa conversazione (un riservato di un altro canale non compare). */
export const recentiDisponibili = (ids: string[], pacchetti: PacchettoSticker[]): StickerVoce[] => {
  const perId = new Map(tuttiGliSticker(pacchetti).map((s) => [s.id, s]));
  return ids.map((id) => perId.get(id)).filter((s): s is StickerVoce => !!s);
};
